/**
 * ReconnectableWebSocket
 * 支持 ping-pong 超时检测 + 指数退避断线重连的 WebSocket 封装
 *
 * 用法:
 *   const rws = new ReconnectableWebSocket({
 *     url: 'ws://xxx?from_uid=123',
 *     pingTimeout: 45000,
 *     buildPongMessage: (parsedData) => ({ pong: { message: { type: 'pong' } } }),
 *     buildLoginMessage: () => ({ agentlogin: { message: { ... } } })
 *   })
 *   rws.onOpen = () => {}
 *   rws.onMessage = (parsedData, rawEvent) => {}
 *   rws.onClose = (event) => {}
 *   rws.onError = (event) => {}
 *   rws.onReconnecting = (attempt, maxAttempts) => {}
 *   rws.onReconnected = () => {}
 *   rws.onReconnectFailed = () => {}
 *   rws.connect()
 */

// 连接状态枚举，供外部组件使用
export const RWS_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected'
}

const DEFAULT_OPTIONS = {
  pingTimeout: 45000,           // 45s, 服务端ping间隔15s, 3个周期未收到消息判定断开
  reconnectBaseDelay: 2000,     // 初始重连延迟 2s
  reconnectMaxDelay: 30000,     // 最大退避延迟 30s
  maxReconnectAttempts: 10,     // 最大重连次数
  jitterRatio: 0.3,             // 退避抖动比例
}

export default class ReconnectableWebSocket {
  /**
   * @param {Object} options
   * @param {string}   options.url                 - WebSocket URL (必填)
   * @param {number}   [options.pingTimeout=45000]  - ping超时毫秒数
   * @param {number}   [options.reconnectBaseDelay=2000]
   * @param {number}   [options.reconnectMaxDelay=30000]
   * @param {number}   [options.maxReconnectAttempts=10]
   * @param {number}   [options.jitterRatio=0.3]
   * @param {Function} [options.buildPongMessage]   - (parsedPingData) => Object, 自定义pong消息体
   * @param {Function} [options.buildLoginMessage]  - () => Object|null, 重连后重发的login消息
   */
  constructor(options) {
    if (!options || !options.url) {
      throw new Error('[RWS] options.url is required')
    }

    // 所有属性用 enumerable:false 定义，防止被 Vue 响应式系统劫持
    // (实例可能被存入 Vuex store，Vue Observer 会遍历 enumerable 属性)

    // 内部状态
    Object.defineProperty(this, '_options', { value: { ...DEFAULT_OPTIONS, ...options }, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_ws', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_pingTimer', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_reconnectTimer', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_reconnectAttempts', { value: 0, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_manualClose', { value: false, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_destroyed', { value: false, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_isReconnect', { value: false, enumerable: false, writable: true, configurable: true })

    // 外部回调
    Object.defineProperty(this, 'onOpen', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onMessage', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onClose', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onError', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onReconnecting', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onReconnected', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onReconnectFailed', { value: null, enumerable: false, writable: true, configurable: true })

    // 状态追踪
    Object.defineProperty(this, 'status', { value: RWS_STATUS.DISCONNECTED, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'reconnectAttempt', { value: 0, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, 'onStatusChange', { value: null, enumerable: false, writable: true, configurable: true })

    // 网络事件句柄引用
    Object.defineProperty(this, '_onOnline', { value: null, enumerable: false, writable: true, configurable: true })
    Object.defineProperty(this, '_onVisibilityChange', { value: null, enumerable: false, writable: true, configurable: true })
  }

  /**
   * 更新状态并通知外部
   * @param {string} newStatus - RWS_STATUS 枚举值
   */
  _setStatus(newStatus) {
    var oldStatus = this.status
    var oldAttempt = this.reconnectAttempt
    this.status = newStatus
    this.reconnectAttempt = this._reconnectAttempts
    // 状态变化 或 重连次数变化 都需要通知外部
    if ((oldStatus !== newStatus || oldAttempt !== this._reconnectAttempts) && this.onStatusChange) {
      try { this.onStatusChange(newStatus, oldStatus, this._reconnectAttempts) } catch (err) { console.error('[RWS] onStatusChange error:', err) }
    }
  }

  // ======================== 公开 API ========================

  /**
   * 建立连接
   */
  connect() {
    this._manualClose = false
    this._destroyed = false
    this._setStatus(RWS_STATUS.CONNECTING)
    this._createConnection()
  }

  /**
   * 发送消息
   * @param {string|Object} data - 字符串或可序列化对象
   * @returns {boolean} 是否发送成功
   */
  send(data) {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(typeof data === 'string' ? data : JSON.stringify(data))
      return true
    }
    console.warn('[RWS] 连接未就绪, 消息未发送')
    return false
  }

  /**
   * 主动关闭连接 (不触发重连)
   */
  close() {
    this._manualClose = true
    this._cleanup()
    if (this._ws) {
      this._ws.close()
    }
    this._setStatus(RWS_STATUS.DISCONNECTED)
  }

  /**
   * 彻底销毁 (组件卸载时调用)
   * 关闭连接、清理定时器、移除网络事件监听、清空回调
   */
  destroy() {
    this._destroyed = true
    this.close()
    this.unbindNetworkEvents()
    this.onOpen = null
    this.onMessage = null
    this.onClose = null
    this.onError = null
    this.onReconnecting = null
    this.onReconnected = null
    this.onReconnectFailed = null
    this.onStatusChange = null
  }

  /**
   * 手动触发重连 (重置重连次数)
   */
  reconnectNow() {
    this._manualClose = false
    this._destroyed = false
    this._reconnectAttempts = 0
    this._cleanup()
    this._setStatus(RWS_STATUS.RECONNECTING)
    this._scheduleReconnect()
  }

  /**
   * 当前连接状态 (同原生 WebSocket.readyState)
   */
  get readyState() {
    return this._ws ? this._ws.readyState : WebSocket.CLOSED
  }

  /**
   * 是否处于已连接状态
   */
  get isConnected() {
    return this._ws && this._ws.readyState === WebSocket.OPEN
  }

  // ======================== 内部实现 ========================

  _createConnection() {
    if (this._destroyed) return

    // 如果旧连接还存在，先清理
    // 已处于连接状态则跳过，防止重复连接
    if (this.status === RWS_STATUS.CONNECTED) {
      console.log('[RWS] 已处于连接状态，跳过 _createConnection')
      return
    }

    if (this._ws) {
      this._ws.onopen = null
      this._ws.onmessage = null
      this._ws.onclose = null
      this._ws.onerror = null
      try { this._ws.close() } catch (e) { /* ignore */ }
    }

    try {
      this._ws = new WebSocket(this._options.url)
    } catch (e) {
      console.error('[RWS] 创建WebSocket失败:', e)
      this._scheduleReconnect()
      return
    }

    this._ws.onopen = (e) => {
      console.log('[RWS] 连接成功')
      this._reconnectAttempts = 0
      this._setStatus(RWS_STATUS.CONNECTED)
      this._startPingCheck()

      // 首次连接和重连都通过 buildLoginMessage 发送登录消息
      this._sendLoginMessage()

      if (this._isReconnect) {
        if (this.onReconnected) {
          try { this.onReconnected() } catch (err) { console.error('[RWS] onReconnected error:', err) }
        }
        this._isReconnect = false
      }

      if (this.onOpen) {
        try { this.onOpen(e) } catch (err) { console.error('[RWS] onOpen error:', err) }
      }
    }

    this._ws.onmessage = (e) => {
      let parsedData = null
      try {
        parsedData = JSON.parse(e.data)
      } catch (_parseErr) {
        // 无法解析为JSON，直接传递给业务层
        if (this.onMessage) {
          try { this.onMessage(null, e) } catch (err) { console.error('[RWS] onMessage error:', err) }
        }
        return
      }

      // ★ 收到任何消息 → 重置ping超时计时器
      this._resetPingCheck()

      // ★ 收到 ping → 自动回 pong
      if (parsedData && parsedData.type === 'ping') {
        const pongMsg = this._options.buildPongMessage
          ? this._options.buildPongMessage(parsedData)
          : { pong: { message: { type: 'pong' } } }
        this.send(pongMsg)
      }

      // 所有消息都传递给业务层 (包括ping, 业务层可选择忽略)
      if (this.onMessage) {
        try { this.onMessage(parsedData, e) } catch (err) { console.error('[RWS] onMessage error:', err) }
      }
    }

    this._ws.onclose = (e) => {
      console.log('[RWS] 连接关闭, code=' + e.code + ', reason=' + e.reason)
      this._stopPingCheck()

      if (this.onClose) {
        try { this.onClose(e) } catch (err) { console.error('[RWS] onClose error:', err) }
      }

      // ★ 非主动关闭 → 触发重连（如果 ping 超时已触发重连则跳过，避免重复）
      if (!this._manualClose && !this._destroyed) {
          this._setStatus(RWS_STATUS.RECONNECTING)
          this._scheduleReconnect()
      } else {
        this._setStatus(RWS_STATUS.DISCONNECTED)
      }
    }

    this._ws.onerror = (e) => {
      console.error('[RWS] 连接错误')
      if (this.onError) {
        try { this.onError(e) } catch (err) { console.error('[RWS] onError error:', err) }
      }
      // onerror 之后浏览器会自动触发 onclose，重连在 onclose 中处理
    }
  }

  // ======================== 心跳超时检测 ========================

  _startPingCheck() {
    this._stopPingCheck()
    this._pingTimer = setTimeout(() => {
      console.warn('[RWS] ' + this._options.pingTimeout + 'ms 未收到服务端消息, 判定连接失活')
      this._setStatus(RWS_STATUS.RECONNECTING)
      // ★ 立即启动重连，不等 onclose（网络断开时 TCP 超时可能要 30~120s）
      // _createConnection 内部会先关闭旧连接再建新连接
      this._scheduleReconnect()
    }, this._options.pingTimeout)
  }

  _resetPingCheck() {
    if (this._pingTimer) {
      this._startPingCheck()
    }
  }

  _stopPingCheck() {
    if (this._pingTimer) {
      clearTimeout(this._pingTimer)
      this._pingTimer = null
    }
  }

  // ======================== 断线重连 ========================

  _scheduleReconnect() {
    if (this._manualClose || this._destroyed) return
    // 已处于连接状态则跳过，防止重复连接
    if (this.status === RWS_STATUS.CONNECTED) return

    if (this._reconnectAttempts >= this._options.maxReconnectAttempts) {
      console.error('[RWS] 达到最大重连次数 (' + this._options.maxReconnectAttempts + '), 停止重连')
      this._setStatus(RWS_STATUS.DISCONNECTED)
      if (this.onReconnectFailed) {
        try { this.onReconnectFailed() } catch (err) { console.error('[RWS] onReconnectFailed error:', err) }
      }
      return
    }

    const { reconnectBaseDelay, reconnectMaxDelay, jitterRatio } = this._options
    const base = Math.min(
      reconnectBaseDelay * Math.pow(2, this._reconnectAttempts),
      reconnectMaxDelay
    )
    const jitter = base * jitterRatio * Math.random()
    const delay = Math.floor(base + jitter)

    this._reconnectAttempts++
    this._isReconnect = true
    this._setStatus(RWS_STATUS.RECONNECTING)

    console.log('[RWS] 第 ' + this._reconnectAttempts + '/' + this._options.maxReconnectAttempts + ' 次重连, ' + delay + 'ms 后执行')
    if (this.onReconnecting) {
      try {
        this.onReconnecting(this._reconnectAttempts, this._options.maxReconnectAttempts)
      } catch (err) {
        console.error('[RWS] onReconnecting error:', err)
      }
    }

    this._reconnectTimer = setTimeout(() => {
      this._createConnection()
    }, delay)
  }

  _sendLoginMessage() {
    if (this._options.buildLoginMessage) {
      const loginMsg = this._options.buildLoginMessage()
      if (loginMsg) {
        this.send(loginMsg)
        console.log('[RWS] 发送login消息' + (this._isReconnect ? '(重连)' : '(首次)'))
      }
    }
  }

  _cleanup() {
    this._stopPingCheck()
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
  }

  // ======================== 网络恢复 & 页面可见性 ========================

  /**
   * 绑定浏览器网络恢复 & 页面可见性事件
   * 网络恢复或页面从后台切回前台时，如果连接已断，立即尝试重连
   * 建议在项目入口调用一次
   */
  bindNetworkEvents() {
    this.unbindNetworkEvents()

    this._onOnline = () => {
      if (!this.isConnected && !this._manualClose && !this._destroyed) {
        console.log('[RWS] 网络恢复, 立即尝试重连')
        this._reconnectAttempts = 0
        this._cleanup()
        this._scheduleReconnect()
      }
    }

    this._onVisibilityChange = () => {
      if (!document.hidden && !this.isConnected && !this._manualClose && !this._destroyed) {
        console.log('[RWS] 页面激活, 检查连接状态并尝试重连')
        this._reconnectAttempts = 0
        this._cleanup()
        this._scheduleReconnect()
      }
    }

    window.addEventListener('online', this._onOnline)
    document.addEventListener('visibilitychange', this._onVisibilityChange)
  }

  /**
   * 移除网络事件监听
   */
  unbindNetworkEvents() {
    if (this._onOnline) {
      window.removeEventListener('online', this._onOnline)
      this._onOnline = null
    }
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange)
      this._onVisibilityChange = null
    }
  }
}
